import { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Database, ChevronLeft, ChevronRight } from 'lucide-react';
import { Stock } from '../data/mockData';
import EmptyState from './EmptyState';

interface StockTableProps {
  stocks: Stock[];
  columns: {
    key: keyof Stock;
    label: string;
    sortable?: boolean;
    render?: (value: any, stock: Stock) => React.ReactNode;
  }[];
  defaultSort?: SortConfig;
  pageSize?: number;
}

type SortConfig = {
  key: keyof Stock;
  direction: 'asc' | 'desc' | null;
};

export default function StockTable({ stocks, columns, defaultSort = { key: 'changePercent', direction: 'desc' }, pageSize = 20 }: StockTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>(defaultSort);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSort = (key: keyof Stock) => {
    let direction: 'asc' | 'desc' | null = 'asc';
    
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }
    
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page on sort change
  };

  const sortedStocks = [...stocks].sort((a, b) => {
    if (!sortConfig.direction) return 0;

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === undefined || bValue === undefined) return 0;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const aStr = String(aValue);
    const bStr = String(bValue);
    
    return sortConfig.direction === 'asc' 
      ? aStr.localeCompare(bStr) 
      : bStr.localeCompare(aStr);
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedStocks.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentStocks = sortedStocks.slice(startIndex, startIndex + pageSize);

  const getSortIcon = (key: keyof Stock) => {
    if (sortConfig.key !== key || !sortConfig.direction) {
      return <ArrowUpDown className="w-3 h-3 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3" />
      : <ArrowDown className="w-3 h-3" />;
  };

  if (stocks.length === 0) {
    return (
      <EmptyState 
        icon={Database}
        title="暂无数据"
        description="当前没有符合条件的股票数据"
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="overflow-x-auto -mx-4 px-4 flex-1">
        <table className="w-full min-w-max">
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={`px-3 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap ${
                    column.sortable !== false ? 'cursor-pointer hover:bg-muted/80' : ''
                  }`}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className="flex items-center gap-1">
                    <span>{column.label}</span>
                    {column.sortable !== false && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {currentStocks.map((stock) => (
              <tr key={stock.id} className="hover:bg-muted/50 transition-colors">
                {columns.map((column) => (
                  <td key={`${stock.id}-${String(column.key)}`} className="px-3 py-3 text-sm whitespace-nowrap">
                    {column.render 
                      ? column.render(stock[column.key], stock)
                      : String(stock[column.key] ?? '-')
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-background">
          <div className="text-sm text-muted-foreground">
            第 {currentPage} 页，共 {totalPages} 页
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}